import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, User, Mail, Phone, Calendar, Clock,
  CreditCard, Save, Edit3, Tag, FileText, Plus, X, Banknote,
  CheckCircle2, AlertCircle, AlertTriangle, MapPin,
  Gift, ArrowUpRight, ArrowDownLeft, Ban, Receipt, Wallet, DollarSign
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import BookingDetail from '@/components/admin/BookingDetail';
import PatientQuickActions from '@/components/admin/PatientQuickActions';
import TransactionDetailPanel from '@/components/admin/TransactionDetailPanel';

const SITE_LABELS = {
  specialist_dental_london: { label: 'SDL', full: 'Specialist Dental London', color: 'bg-[#C8A86B]/15 text-[#96793E]' },
  shihab_romeed: { label: 'SR', full: 'Shihab Romeed', color: 'bg-stone-100 text-stone-700' },
  london_dental_academy: { label: 'LDA', full: 'London Dental Academy', color: 'bg-blue-50 text-blue-700' },
};

const STATUS_STYLES = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  no_show: 'bg-red-100 text-red-800 border-red-300',
};

const PAYMENT_STYLES = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unpaid: 'bg-amber-50 text-amber-700 border-amber-200',
  partially_paid: 'bg-orange-50 text-orange-700 border-orange-200',
  refunded: 'bg-purple-50 text-purple-700 border-purple-200',
  not_required: 'bg-stone-50 text-stone-500 border-stone-200',
};

export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiFetch } = useAdmin();
  const { createDraft } = useBookingDraft();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditAction, setCreditAction] = useState('add'); // 'add' | 'refund'
  const [selectedTx, setSelectedTx] = useState(null);

  const loadPatient = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/patients/${id}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPatient(data.patient);
      setNotes(data.patient.notes || '');
    } catch {
      toast.error('Failed to load patient');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => { loadPatient(); }, [loadPatient]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const res = await apiFetch(`/patients/${id}`, { method: 'PUT', body: { notes } });
      if (!res.ok) throw new Error();
      toast.success('Notes saved');
      setEditingNotes(false);
      setPatient(p => ({ ...p, notes }));
    } catch { toast.error('Failed to save notes'); }
    finally { setSavingNotes(false); }
  };

  if (loading) {
    return (
      <div className="max-w-6xl space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          <Skeleton className="h-[600px] rounded-2xl" />
          <Skeleton className="h-[400px] rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="max-w-6xl">
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-900">Patient Not Found</h3>
            <Link to="/admin/patients"><Button className="mt-6">Back to Patients</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const site = SITE_LABELS[patient.siteKey] || { label: patient.siteKey, color: 'bg-stone-100 text-stone-600' };
  const bookings = patient.bookings || [];
  const payments = patient.payments || [];
  const totalPaid = patient.financialSummary?.totalPaid ?? 0;
  const totalBookings = bookings.length;
  const unpaidBookings = bookings.filter(b => b.balance > 0);
  const outstandingBalance = patient.financialSummary?.outstandingBalance ?? 0;
  const noShowCount = bookings.filter(b => b.status === 'no_show').length;
  const creditBalance = patient.financialSummary?.creditBalance ?? 0;
  const totalRefunded = patient.financialSummary?.totalRefunded ?? 0;
  const lastVisit = bookings.find(b => b.status === 'completed');
  const lastVisitDate = lastVisit ? new Date(lastVisit.confirmedDate || lastVisit.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  // Build unified timeline from bookings + payments
  const timeline = [];
  bookings.forEach(b => {
    timeline.push({
      id: b.id,
      type: 'booking',
      date: b.confirmedDate || b.createdAt,
      time: b.confirmedTime,
      data: b,
    });
  });
  payments.forEach(p => {
    timeline.push({
      id: p.id,
      type: 'payment',
      date: p.createdAt,
      data: p,
    });
  });
  timeline.sort((a, b) => new Date(b.date) - new Date(a.date));

  const age = patient.dateOfBirth ? Math.floor((Date.now() - new Date(patient.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : null;

  return (
    <div className="max-w-6xl pb-20">
      {/* Back + Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/patients" className="h-10 w-10 rounded-full border border-stone-200 bg-white flex items-center justify-center hover:bg-stone-50 transition-colors shadow-sm shrink-0">
          <ArrowLeft className="w-4 h-4 text-stone-600" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Patient Profile</p>
            <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider', site.color)}>
              {site.label}
            </span>
            {noShowCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">
                <AlertTriangle className="w-3 h-3" /> {noShowCount} no-show{noShowCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h2 className="text-2xl md:text-3xl font-playfair font-medium text-stone-900 tracking-tight truncate">
            {patient.firstName} {patient.lastName}
          </h2>
        </div>
        <div className="shrink-0">
          <PatientQuickActions patient={patient} />
        </div>
      </div>

      {/* Contact Info Bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-stone-600 bg-white border border-stone-200 rounded-xl p-4 shadow-sm">
        {patient.email && (
          <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-stone-400" /> {patient.email}</span>
        )}
        {patient.phone && (
          <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-stone-400" /> {patient.phone}</span>
        )}
        {patient.dateOfBirth && (
          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-stone-400" /> {patient.dateOfBirth}{age != null && ` (${age}y)`}</span>
        )}
        {patient.tags?.length > 0 && (
          <span className="flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-stone-400" />
            {patient.tags.map(t => (
              <span key={t} className="px-1.5 py-0.5 bg-stone-100 text-stone-600 text-[10px] font-medium rounded">{t}</span>
            ))}
          </span>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-6">
        <MiniStat label="Bookings" value={totalBookings} />
        <MiniStat label="Total Paid" value={`£${totalPaid.toFixed(2)}`} />
        <MiniStat label="Outstanding" value={`£${outstandingBalance.toFixed(2)}`} warn={outstandingBalance > 0} />
        <MiniStat label="Refunded" value={`£${totalRefunded.toFixed(2)}`} subtle={totalRefunded === 0} />
        <MiniStat label="Account Credit" value={`£${creditBalance.toFixed(2)}`} accent={creditBalance > 0} />
        <MiniStat label="No-shows" value={noShowCount} warn={noShowCount > 0} />
      </div>

      {/* Main Content: Timeline + Sidebar */}
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        {/* Left: Activity Timeline */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-stone-900">Activity Timeline</h3>

          {timeline.length === 0 ? (
            <Card className="border-stone-200 shadow-sm">
              <CardContent className="py-12 text-center">
                <Calendar className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                <p className="text-stone-500 text-sm">No activity yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {timeline.map(entry => (
                <TimelineEntry
                  key={`${entry.type}-${entry.id}`}
                  entry={entry}
                  onClickBooking={setSelectedBookingId}
                  onClickTx={setSelectedTx}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-5">
          {/* Quick Actions */}
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-4 space-y-2">
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Quick Actions</p>
              <Button
                variant="outline"
                className="w-full h-9 justify-start gap-2 text-sm border-stone-200"
                onClick={() => createDraft({ patientId: patient.id, patientName: `${patient.firstName} ${patient.lastName}`, siteKey: patient.siteKey })}
              >
                <Plus className="w-3.5 h-3.5" /> New Booking
              </Button>
              {unpaidBookings.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full h-9 justify-start gap-2 text-sm border-amber-200 text-amber-700 hover:bg-amber-50"
                  onClick={() => setPaymentModalOpen(true)}
                >
                  <Banknote className="w-3.5 h-3.5" /> Record Payment ({unpaidBookings.length} unpaid)
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full h-9 justify-start gap-2 text-sm border-stone-200"
                onClick={() => setChargeModalOpen(true)}
              >
                <CreditCard className="w-3.5 h-3.5" /> Charge Patient
              </Button>
            </CardContent>
          </Card>

          {/* Credit Management */}
          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <div className={cn('p-4', creditBalance > 0 ? 'bg-gradient-to-br from-violet-50 to-violet-100/50' : '')}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> Account Credit
                </p>
                <span className={cn('text-lg font-bold', creditBalance > 0 ? 'text-violet-700' : 'text-stone-300')}>
                  £{creditBalance.toFixed(2)}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50"
                  onClick={() => { setCreditAction('add'); setCreditModalOpen(true); }}
                >
                  <Plus className="w-3 h-3" /> Add Credit
                </Button>
                {creditBalance > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-8 text-xs gap-1.5 border-stone-200 text-stone-600 hover:bg-stone-50"
                    onClick={() => { setCreditAction('refund'); setCreditModalOpen(true); }}
                  >
                    <ArrowUpRight className="w-3 h-3" /> Refund Credit
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Internal Notes — always visible */}
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Internal Notes
                </p>
                {!editingNotes ? (
                  <button onClick={() => setEditingNotes(true)} className="text-xs font-medium text-stone-500 hover:text-stone-900">Edit</button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingNotes(false); setNotes(patient.notes || ''); }} className="text-xs font-medium text-stone-400 hover:text-stone-600">Cancel</button>
                    <button onClick={saveNotes} disabled={savingNotes} className="text-xs font-medium text-stone-900 hover:text-stone-700">
                      {savingNotes ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              {editingNotes ? (
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={5}
                  className="w-full rounded-lg border border-stone-200 text-sm px-3 py-2 resize-none focus:ring-2 focus:ring-stone-900 focus:border-transparent"
                  placeholder="Add notes about this patient..."
                  autoFocus
                />
              ) : (
                <div className="bg-stone-50 rounded-lg border border-stone-200 p-3 min-h-[80px]">
                  {patient.notes ? (
                    <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{patient.notes}</p>
                  ) : (
                    <p className="text-sm text-stone-400 italic">No notes yet.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          {patient.tags?.length > 0 && (
            <Card className="border-stone-200 shadow-sm">
              <CardContent className="p-4">
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> Tags
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {patient.tags.map(t => (
                    <span key={t} className="px-2.5 py-1 bg-stone-100 text-stone-700 text-xs font-medium rounded-md border border-stone-200">{t}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Outstanding Balance Alert */}
          {outstandingBalance > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-semibold text-amber-800">Outstanding Balance</p>
              </div>
              <p className="text-2xl font-bold text-amber-900">£{outstandingBalance.toFixed(2)}</p>
              <p className="text-xs text-amber-700 mt-1">{unpaidBookings.length} unpaid booking{unpaidBookings.length !== 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      </div>

      {/* Booking Detail Slide-over */}
      <AnimatePresence>
        {selectedBookingId && (
          <BookingDetail
            bookingId={selectedBookingId}
            onClose={() => setSelectedBookingId(null)}
            onUpdate={() => loadPatient(true)}
          />
        )}
      </AnimatePresence>

      {/* Manual Payment Modal */}
      <AnimatePresence>
        {paymentModalOpen && (
          <RecordPaymentModal
            bookings={unpaidBookings}
            onClose={() => setPaymentModalOpen(false)}
            onSuccess={() => { setPaymentModalOpen(false); loadPatient(true); }}
          />
        )}
      </AnimatePresence>

      {/* Charge Patient Modal */}
      <AnimatePresence>
        {chargeModalOpen && (
          <ChargePatientModal
            patient={patient}
            bookings={bookings}
            onClose={() => setChargeModalOpen(false)}
            onSuccess={() => { setChargeModalOpen(false); loadPatient(true); }}
          />
        )}
      </AnimatePresence>

      {/* Credit Management Modal */}
      <AnimatePresence>
        {creditModalOpen && (
          <CreditManagementModal
            patient={patient}
            action={creditAction}
            onClose={() => setCreditModalOpen(false)}
            onSuccess={() => { setCreditModalOpen(false); loadPatient(true); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedTx && (
          <TransactionDetailPanel
            tx={selectedTx}
            apiFetch={apiFetch}
            onClose={() => setSelectedTx(null)}
            onUpdate={() => { setSelectedTx(null); loadPatient(true); }}
            navigate={navigate}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Timeline Entry ── */
function TimelineEntry({ entry, onClickBooking, onClickTx }) {
  const { type, data } = entry;

  if (type === 'booking') {
    const b = data;
    return (
      <motion.button
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => onClickBooking(b.id)}
        className="w-full text-left bg-white border border-stone-200 rounded-xl p-4 hover:border-stone-300 hover:shadow-sm transition-all group"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize', STATUS_STYLES[b.status] || 'bg-stone-50')}>
                {b.status?.replace('_', ' ')}
              </span>
              <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize', PAYMENT_STYLES[b.paymentStatus] || 'bg-stone-50')}>
                {b.paymentStatus?.replace('_', ' ')}
              </span>
              <span className="font-mono text-[10px] text-stone-400">{b.reference}</span>
            </div>
            <p className="text-sm font-medium text-stone-900 truncate">{b.service}</p>
            {b.provider?.name && <p className="text-xs text-stone-500 mt-0.5">with {b.provider.name}</p>}
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm font-semibold text-stone-900">
              {'\u00A3'}{b.amountPaid || 0} <span className="text-stone-400 font-normal">/ {'\u00A3'}{b.amountCharged || b.depositAmount || 0}</span>
            </p>
            <p className="text-[11px] text-stone-500 mt-0.5">
              {b.confirmedDate ? new Date(b.confirmedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD'}
              {b.confirmedTime && ` \u00B7 ${b.confirmedTime}`}
            </p>
          </div>
        </div>
      </motion.button>
    );
  }

  if (type === 'payment') {
    const p = data;
    const isManual = p.stripeSessionId?.startsWith('manual-');
    const txType = p.type || 'payment';

    let txConfig = {
      payment: { icon: ArrowDownLeft, bg: 'bg-emerald-50', iconColor: 'text-emerald-600', label: 'Payment', amountPrefix: '+' },
      refund: { icon: ArrowUpRight, bg: 'bg-red-50', iconColor: 'text-red-600', label: p.metadata?.method === 'credit' ? 'Converted to Credit' : 'Refund', amountPrefix: '−' },
      credit: { icon: Gift, bg: 'bg-violet-50', iconColor: 'text-violet-600', label: 'Credit Applied', amountPrefix: '+' },
      void: { icon: Ban, bg: 'bg-stone-100', iconColor: 'text-stone-400', label: 'Void', amountPrefix: '' },
      adjustment: { icon: Receipt, bg: 'bg-blue-50', iconColor: 'text-blue-600', label: 'Charge Adjusted', amountPrefix: '±' },
    };
    
    let config = txConfig[txType] || txConfig.payment;
    const isReversal = txType === 'void';
    const isVoided = p.voidedById || p.status === 'voided';

    if (isReversal) {
      config = { ...txConfig.void, label: 'Void (Reversal)', amountPrefix: '' };
    }
    
    const Icon = config.icon;

    return (
      <motion.button
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => onClickTx(p)}
        className={cn('w-full text-left bg-white border rounded-xl p-3 pl-4 flex items-center gap-3 transition-all hover:border-stone-300 hover:shadow-sm',
          (isVoided || isReversal) ? 'border-stone-100 opacity-50 bg-stone-50/50' : 'border-stone-200')}
      >
        <div className={cn('h-8 w-8 rounded-full flex items-center justify-center shrink-0', config.bg)}>
          <Icon className={cn('w-4 h-4', config.iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-stone-700">
            <span className={cn('font-semibold', (isVoided || isReversal) ? 'line-through text-stone-400' : config.iconColor)}>{config.amountPrefix}£{p.amount?.toFixed(2)}</span>
            {' '}<span className={cn((isVoided || isReversal) ? 'text-stone-400' : 'text-stone-500')}>{config.label}</span>
            {isVoided && !isReversal && (
              <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-500 uppercase tracking-wider">VOIDED</span>
            )}
            {p.status !== 'completed' && !isVoided && !isReversal && (
              <span className={cn('ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider', p.status === 'expired' ? 'bg-stone-100 text-stone-500' : 'bg-amber-50 text-amber-600')}>{p.status}</span>
            )}
            {p.bookingReference && <span className="text-stone-400"> · {p.bookingReference}</span>}
          </p>
          <p className="text-[11px] text-stone-400">
            {isManual ? (p.metadata?.method || 'manual').replace('_', ' ') : txType === 'credit' ? 'From account credit' : txType === 'refund' ? (p.metadata?.method === 'credit' ? 'To account credit' : p.metadata?.method || 'Refund') : 'Stripe'}
            {' · '}{new Date(p.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            {p.recordedBy && <span> · by {p.recordedBy}</span>}
          </p>
          {p.reason && <p className="text-[10px] text-stone-400 mt-0.5 italic truncate">{p.reason}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isVoided && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-50 text-red-500 border border-red-100">VOIDED</span>
          )}
        </div>
      </motion.button>
    );
  }

  return null;
}

/* ── Mini Stat ── */
function MiniStat({ label, value, warn, accent, subtle }) {
  return (
    <div className={cn('rounded-xl border p-3 shadow-sm transition-all',
      warn ? 'bg-amber-50 border-amber-200' :
      accent ? 'bg-violet-50 border-violet-200' :
      subtle ? 'bg-stone-50/50 border-stone-100' :
      'bg-white border-stone-200'
    )}>
      <p className={cn('text-[10px] font-semibold uppercase tracking-wider',
        warn ? 'text-amber-500' : accent ? 'text-violet-500' : subtle ? 'text-stone-300' : 'text-stone-400'
      )}>{label}</p>
      <p className={cn('text-lg font-bold mt-0.5',
        warn ? 'text-amber-800' : accent ? 'text-violet-700' : subtle ? 'text-stone-300' : 'text-stone-900'
      )}>{value}</p>
    </div>
  );
}

/* ── Record Payment Modal ── */
function RecordPaymentModal({ bookings, onClose, onSuccess }) {
  const { apiFetch } = useAdmin();
  const [selectedBookingId, setSelectedBookingId] = useState(bookings[0]?.id || '');
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedBooking = bookings.find(b => b.id === selectedBookingId);

  useEffect(() => {
    if (selectedBooking) setAmount(String(selectedBooking.balance || 0));
  }, [selectedBookingId, selectedBooking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBookingId || !amount) return;
    setSaving(true);
    try {
      const res = await apiFetch('/payments/manual', {
        method: 'POST',
        body: { bookingId: selectedBookingId, amount: parseFloat(amount), method, notes: paymentNotes },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record payment');
      toast.success('Payment recorded successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><Banknote className="w-5 h-5 text-emerald-600" /></div>
                <div><h3 className="font-playfair text-lg font-medium text-stone-900">Record Payment</h3><p className="text-xs text-stone-500">Log a manual payment</p></div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400"><X size={18} /></button>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Booking</label>
              <select value={selectedBookingId} onChange={e => setSelectedBookingId(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3">
                {bookings.map(b => (<option key={b.id} value={b.id}>{b.reference} — {b.service} (Owes {'\u00A3'}{b.balance})</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-stone-700">Amount ({'\u00A3'})</label>
                <Input type="number" step="0.01" min="0" required value={amount} onChange={e => setAmount(e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-stone-700">Method</label>
                <select value={method} onChange={e => setMethod(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3">
                  <option value="cash">Cash</option><option value="card">Card (Manual)</option><option value="bank_transfer">Bank Transfer</option><option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Notes (optional)</label>
              <textarea value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 text-sm px-3 py-2 resize-none" placeholder="e.g. Paid in cash at reception" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
              <Button type="submit" disabled={saving || !selectedBookingId} className="h-10 bg-emerald-600 text-white hover:bg-emerald-700 gap-2">
                {saving ? 'Recording...' : <><CheckCircle2 className="w-4 h-4" /> Confirm</>}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </>
  );
}

/* ── Charge Patient Modal ── */
function ChargePatientModal({ bookings, onClose, onSuccess }) {
  const { apiFetch } = useAdmin();
  const [selectedBookingId, setSelectedBookingId] = useState(bookings[0]?.id || '');
  const [amountCharged, setAmountCharged] = useState('');
  const [sendPaymentLink, setSendPaymentLink] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedBooking = bookings.find(b => b.id === selectedBookingId);

  useEffect(() => {
    if (selectedBooking) setAmountCharged(String(selectedBooking.amountCharged));
  }, [selectedBookingId, selectedBooking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBookingId || !amountCharged) return;
    setSaving(true);
    try {
      const res = await apiFetch('/payments/charge', {
        method: 'POST',
        body: { bookingId: selectedBookingId, amountCharged: parseFloat(amountCharged), sendPaymentLink },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update charge');
      toast.success('Charge updated');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center"><Banknote className="w-5 h-5 text-stone-600" /></div>
                <div><h3 className="font-playfair text-lg font-medium text-stone-900">Charge Patient</h3><p className="text-xs text-stone-500">Adjust the amount owed</p></div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400"><X size={18} /></button>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Booking</label>
              <select value={selectedBookingId} onChange={e => setSelectedBookingId(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3">
                {bookings.map(b => (<option key={b.id} value={b.id}>{b.reference} — {b.service} ({'\u00A3'}{b.amountCharged})</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Final Amount ({'\u00A3'})</label>
              <Input type="number" step="0.01" min="0" required value={amountCharged} onChange={e => setAmountCharged(e.target.value)} className="h-10" />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <input type="checkbox" id="sendPaymentLink2" checked={sendPaymentLink} onChange={e => setSendPaymentLink(e.target.checked)} className="rounded border-stone-300" />
              <label htmlFor="sendPaymentLink2" className="text-sm text-stone-700 cursor-pointer">Send payment link (if balance remains)</label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
              <Button type="submit" disabled={saving || !selectedBookingId} className="h-10 bg-stone-900 text-white hover:bg-stone-800 gap-2">
                {saving ? 'Saving...' : <><CheckCircle2 className="w-4 h-4" /> Update Charge</>}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </>
  );
}

/* ── Credit Management Modal ── */
function CreditManagementModal({ patient, action, onClose, onSuccess }) {
  const { apiFetch } = useAdmin();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState('cash');
  const [saving, setSaving] = useState(false);

  const isAdd = action === 'add';
  const creditAvailable = (patient.creditBalancePence || 0) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !reason) return;
    setSaving(true);
    try {
      const endpoint = isAdd ? '/payments/patient-credit/add' : '/payments/patient-credit/refund';
      const body = { patientId: patient.id, amount: parseFloat(amount), reason };
      if (!isAdd) body.method = method;
      const res = await apiFetch(endpoint, { method: 'POST', body });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      toast.success(isAdd ? `£${parseFloat(amount).toFixed(2)} credit added` : `£${parseFloat(amount).toFixed(2)} credit refunded`);
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className={cn('p-6 border-b', isAdd ? 'border-violet-100 bg-gradient-to-r from-violet-50 to-white' : 'border-stone-100')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', isAdd ? 'bg-violet-100' : 'bg-stone-100')}>
                  {isAdd ? <Gift className="w-5 h-5 text-violet-600" /> : <ArrowUpRight className="w-5 h-5 text-stone-600" />}
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-medium text-stone-900">{isAdd ? 'Add Credit' : 'Refund Credit'}</h3>
                  <p className="text-xs text-stone-500">{isAdd ? 'Add credit to patient account' : `Available: £${creditAvailable.toFixed(2)}`}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400"><X size={18} /></button>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Amount (£)</label>
              <Input
                type="number" step="0.01" min="0.01"
                max={!isAdd ? creditAvailable : undefined}
                required value={amount}
                onChange={e => setAmount(e.target.value)}
                className="h-10" placeholder="0.00"
              />
              {!isAdd && parseFloat(amount) > creditAvailable && (
                <p className="text-xs text-red-500">Exceeds available credit (£{creditAvailable.toFixed(2)})</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Reason</label>
              <textarea
                value={reason} onChange={e => setReason(e.target.value)}
                rows={2} required
                className="w-full rounded-lg border border-stone-200 text-sm px-3 py-2 resize-none focus:ring-2 focus:ring-stone-900 focus:border-transparent"
                placeholder={isAdd ? 'e.g. Goodwill gesture, compensation, loyalty reward' : 'e.g. Patient requested refund of credit balance'}
              />
            </div>
            {!isAdd && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-stone-700">Refund Method</label>
                <select value={method} onChange={e => setMethod(e.target.value)} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3">
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="h-10">Cancel</Button>
              <Button
                type="submit"
                disabled={saving || !amount || !reason || (!isAdd && parseFloat(amount) > creditAvailable)}
                className={cn('h-10 gap-2', isAdd ? 'bg-violet-600 text-white hover:bg-violet-700' : 'bg-stone-900 text-white hover:bg-stone-800')}
              >
                {saving ? 'Processing...' : <><CheckCircle2 className="w-4 h-4" /> {isAdd ? 'Add Credit' : 'Refund Credit'}</>}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </>
  );
}
